export const PROFILE_COMPLETION_SECTION_ID = "profile-completion";

export type ProfileCompletionStepId = "photo" | "identity" | "bank" | "description" | "phone";

export interface ProfileCompletionStep {
    id: ProfileCompletionStepId;
    label: string;
    completed: boolean;
    optional?: boolean;
}

export interface ProviderProfileCompletionInput {
    pictureId?: string | null;
    identityVerified: boolean;
    bankDetailsComplete: boolean;
    hasDescription: boolean;
    hasPhone: boolean;
}

export interface ProfileCompletionSummary {
    steps: ProfileCompletionStep[];
    completedCount: number;
    totalCount: number;
    percentage: number;
    remainingSteps: number;
    /** All steps complete, including optional identity verification (100%). */
    allComplete: boolean;
    /** Required steps complete; services can be activated without identity verification. */
    requiredComplete: boolean;
}

export function isProviderBankDetailsComplete(
    stripeId: string | undefined | null,
    transfersStatus: string | undefined
): boolean {
    return Boolean(stripeId) && transfersStatus === "active";
}

const PROFILE_COMPLETION_CIRCLE_LENGTH = 175.929;

export function buildProviderProfileCompletionSteps(
    input: ProviderProfileCompletionInput
): ProfileCompletionStep[] {
    return [
        {
            id: "photo",
            label: "Photo de profil",
            completed: Boolean(input.pictureId),
        },
        {
            id: "identity",
            label: "Vérification d'identité (badge vérifié)",
            completed: input.identityVerified,
            optional: true,
        },
        {
            id: "bank",
            label: "RIB & Informations bancaires",
            completed: input.bankDetailsComplete,
        },
        {
            id: "description",
            label: "Description détaillée",
            completed: input.hasDescription,
        },
        {
            id: "phone",
            label: "Numéro de téléphone",
            completed: input.hasPhone,
        },
    ];
}

export function getProviderProfileCompletionSummary(
    input: ProviderProfileCompletionInput
): ProfileCompletionSummary {
    const steps = buildProviderProfileCompletionSteps(input);
    const requiredSteps = steps.filter((step) => !step.optional);
    const completedCount = steps.filter((step) => step.completed).length;
    const totalCount = steps.length;
    const remainingSteps = totalCount - completedCount;
    const percentage = totalCount === 0 ? 0 : Math.round((completedCount / totalCount) * 100);
    const requiredComplete = requiredSteps.every((step) => step.completed);

    return {
        steps,
        completedCount,
        totalCount,
        percentage,
        remainingSteps,
        allComplete: remainingSteps === 0,
        requiredComplete,
    };
}

export function getProfileCompletionStrokeDasharray(percentage: number): string {
    const progress = Math.min(100, Math.max(0, percentage)) / 100;
    return `${progress * PROFILE_COMPLETION_CIRCLE_LENGTH} ${PROFILE_COMPLETION_CIRCLE_LENGTH}`;
}

export function formatRemainingStepsLabel(remainingSteps: number): string {
    if (remainingSteps <= 0) {
        return "Profil complet";
    }
    if (remainingSteps === 1) {
        return "1 étape restante";
    }
    return `${remainingSteps} étapes restantes`;
}

export function isServiceEffectivelyActive(
    isActive: boolean,
    requiredProfileComplete: boolean
): boolean {
    return isActive && requiredProfileComplete;
}
